/**
 * Pull-Based Observability System
 * 
 * Silent probe recording with on-demand retrieval.
 * No console output by default - logs are captured to memory and pulled via API.
 * 
 * Usage:
 *   import { logger } from './core/Logger';
 *   
 *   // Record probes (silent - no console output)
 *   logger.probe('physics:body:create', 'player');
 *   logger.probe('combat:attack:start', 'daniel→zombie');
 *   
 *   // Pull logs by pattern (prints to console, returns entries)
 *   logger.pull('physics:*');                    // All physics probes
 *   logger.pull('combat:attack:*');              // Attack-related
 *   logger.pull('*', { last: 50 });              // Last 50 entries
 *   logger.pull('physics:*', { clear: true });   // Pull and clear
 *   
 *   // Clear buffer
 *   logger.clear();                              // Clear all
 *   logger.clear('physics:*');                   // Clear matching
 *   
 *   // Stream to console in real-time (for interactive debugging)
 *   logger.stream('combat:*');
 *   logger.stopStream();
 *   
 *   // Scene graph capture
 *   logger.scene();                              // Full graph
 *   logger.scene({ include: ['position'] });    // With specific annotations
 *   logger.scene({ around: 'player', depth: 2 }); // N-ring around node
 * 
 * Playwright Usage:
 *   await page.evaluate(() => logger.pull('physics:*'));
 *   await page.evaluate(() => logger.scene({ around: 'player', depth: 2 }));
 * 
 * DEPRECATED: Old scoped API (logger.physics.log()) still works but maps to probes.
 * Migrate to logger.probe() for new code.
 */

import { Scene } from '@babylonjs/core/scene';
import { Node } from '@babylonjs/core/node';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

// ==================== Types ====================

export interface ProbeEntry {
  probe: string;
  time: number;  // Relative time from first log (seconds)
  msg: string;
}

export interface PullOptions {
  last?: number;      // Only return last N entries
  clear?: boolean;    // Clear matching entries after pull
}

export type SceneAnnotation = 
  | 'position' 
  | 'rotation' 
  | 'scaling' 
  | 'visibility' 
  | 'physics' 
  | 'state' 
  | 'metadata';

export interface SceneOptions {
  include?: SceneAnnotation[];  // Which annotations to include
  around?: string;              // Node name for n-ring extraction
  depth?: number;               // Depth for n-ring (default: full graph or 1 for around)
}

export interface SceneNode {
  name: string;
  [key: string]: any;  // Annotations
  children?: SceneNode[];
}

// ==================== Backward Compatibility Types ====================
// These types are DEPRECATED but kept for existing code.

export type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';
export type LogScope =
  | 'core' | 'physics' | 'animation' | 'character' | 'player' | 'npc'
  | 'camera' | 'spline' | 'level' | 'assets' | 'ui' | 'triggers'
  | 'input' | 'performance' | 'debug' | 'combat' | 'environment' | 'behavior' | 'user';

export interface UserLogEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

const ALL_SCOPES: LogScope[] = [
  'core', 'physics', 'animation', 'character', 'player', 'npc',
  'camera', 'spline', 'level', 'assets', 'ui', 'triggers',
  'input', 'performance', 'debug', 'combat', 'environment', 'behavior', 'user',
];

// ==================== Glob Matching ====================

function matchGlob(pattern: string, probe: string): boolean {
  // Convert glob pattern to regex
  // 'physics:*' matches 'physics:body:create', 'physics:collision'
  // '*:error' matches 'combat:error', 'physics:error'
  // '*' matches everything
  
  if (pattern === '*') return true;
  
  const regexPattern = pattern
    .split(':')
    .map(part => part === '*' ? '[^:]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join(':');
  
  // Allow * at end to match any remaining segments
  const finalPattern = pattern.endsWith(':*') 
    ? regexPattern.replace(/\[\^:\]\+$/, '.*')
    : `^${regexPattern}$`;
  
  try {
    return new RegExp(finalPattern).test(probe);
  } catch {
    return false;
  }
}

// ==================== Deprecated Scoped Logger ====================

/**
 * @deprecated Use logger.probe() instead
 */
class DeprecatedScopedLogger {
  constructor(private scope: string, private loggerRef: () => Logger) {}
  
  log(...args: any[]): void {
    this.loggerRef().probe(`${this.scope}:log`, args.map(a => String(a)).join(' '));
  }
  warn(...args: any[]): void {
    this.loggerRef().probe(`${this.scope}:warn`, args.map(a => String(a)).join(' '));
  }
  error(...args: any[]): void {
    this.loggerRef().probe(`${this.scope}:error`, args.map(a => String(a)).join(' '));
  }
  debug(...args: any[]): void {
    this.loggerRef().probe(`${this.scope}:debug`, args.map(a => String(a)).join(' '));
  }
  info(...args: any[]): void {
    this.loggerRef().probe(`${this.scope}:info`, args.map(a => String(a)).join(' '));
  }
}

// ==================== Logger Class ====================

class Logger {
  private buffer: ProbeEntry[] = [];
  private startTime: number = 0;
  private streamPattern: string | null = null;
  private _scene: Scene | null = null;

  // DEPRECATED: Scoped loggers for backward compatibility - use logger.probe() instead
  /** @deprecated Use `logger.probe('core:...', msg)` instead */
  readonly core = new DeprecatedScopedLogger('core', () => this);
  /** @deprecated Use `logger.probe('physics:...', msg)` instead */
  readonly physics = new DeprecatedScopedLogger('physics', () => this);
  /** @deprecated Use `logger.probe('animation:...', msg)` instead */
  readonly animation = new DeprecatedScopedLogger('animation', () => this);
  /** @deprecated Use `logger.probe('character:...', msg)` instead */
  readonly character = new DeprecatedScopedLogger('character', () => this);
  /** @deprecated Use `logger.probe('player:...', msg)` instead */
  readonly player = new DeprecatedScopedLogger('player', () => this);
  /** @deprecated Use `logger.probe('npc:...', msg)` instead */
  readonly npc = new DeprecatedScopedLogger('npc', () => this);
  /** @deprecated Use `logger.probe('camera:...', msg)` instead */
  readonly camera = new DeprecatedScopedLogger('camera', () => this);
  /** @deprecated Use `logger.probe('spline:...', msg)` instead */
  readonly spline = new DeprecatedScopedLogger('spline', () => this);
  /** @deprecated Use `logger.probe('level:...', msg)` instead */
  readonly level = new DeprecatedScopedLogger('level', () => this);
  /** @deprecated Use `logger.probe('assets:...', msg)` instead */
  readonly assets = new DeprecatedScopedLogger('assets', () => this);
  /** @deprecated Use `logger.probe('ui:...', msg)` instead */
  readonly ui = new DeprecatedScopedLogger('ui', () => this);
  /** @deprecated Use `logger.probe('triggers:...', msg)` instead */
  readonly triggers = new DeprecatedScopedLogger('triggers', () => this);
  /** @deprecated Use `logger.probe('input:...', msg)` instead */
  readonly input = new DeprecatedScopedLogger('input', () => this);
  /** @deprecated Use `logger.probe('performance:...', msg)` instead */
  readonly performance = new DeprecatedScopedLogger('performance', () => this);
  /** @deprecated Use `logger.probe('debug:...', msg)` instead */
  readonly debug = new DeprecatedScopedLogger('debug', () => this);
  /** @deprecated Use `logger.probe('combat:...', msg)` instead */
  readonly combat = new DeprecatedScopedLogger('combat', () => this);
  /** @deprecated Use `logger.probe('environment:...', msg)` instead */
  readonly environment = new DeprecatedScopedLogger('environment', () => this);
  /** @deprecated Use `logger.probe('behavior:...', msg)` instead */
  readonly behavior = new DeprecatedScopedLogger('behavior', () => this);
  /** @deprecated Use `logger.probe('user:...', msg)` instead */
  readonly user = new DeprecatedScopedLogger('user', () => this);

  constructor() {
    this.startTime = performance.now();
  }

  // ==================== DEPRECATED Methods ====================
  
  /** @deprecated No-op for backward compatibility */
  userInfo(...args: any[]): void { this.probe('user:info', args.join(' ')); }
  /** @deprecated No-op for backward compatibility */
  userWarn(...args: any[]): void { this.probe('user:warn', args.join(' ')); }
  /** 
   * Log a critical user-facing error. These always print to console!
   * Use for startup failures, asset loading errors, and other blocking issues.
   */
  userError(...args: any[]): void { 
    const message = args.map(a => a instanceof Error ? a.stack || a.message : String(a)).join(' ');
    this.probe('user:error', message);
    // ALWAYS print critical errors to console - these are startup/loading failures!
    console.error('[CRITICAL]', ...args);
  }
  /** @deprecated No-op for backward compatibility */
  enable(..._scopes: LogScope[]): void {}
  /** @deprecated No-op for backward compatibility */
  disable(..._scopes: LogScope[]): void {}
  /** @deprecated No-op for backward compatibility */
  enableAll(): void {}
  /** @deprecated No-op for backward compatibility */
  disableAll(): void {}
  /** @deprecated No-op for backward compatibility */
  getEnabled(): Array<{ scope: LogScope; minLevel: LogLevel }> { return []; }
  /** @deprecated No-op for backward compatibility */
  getAllScopes(): LogScope[] { return ALL_SCOPES; }
  /** @deprecated No-op for backward compatibility */
  setFilter(_scope: LogScope, _level: LogLevel | null): void {}
  /** @deprecated No-op for backward compatibility */
  applyFilterString(_filterStr: string): void {}
  /** @deprecated No-op for backward compatibility */
  getFilterString(): string { return ''; }
  /** @deprecated No-op for backward compatibility */
  resetToDefault(): void {}
  /** @deprecated No-op for backward compatibility */
  getDefaultFilter(): string { return ''; }
  /** @deprecated No-op for backward compatibility */
  showConfig(): void {}
  /** @deprecated No-op for backward compatibility */
  getUserBuffer(): UserLogEntry[] { return []; }
  /** @deprecated No-op for backward compatibility */
  clearUserBuffer(): void {}
  /** @deprecated No-op for backward compatibility */
  setEmoji(_enabled: boolean): void {}
  /** @deprecated No-op for backward compatibility */
  setTimestamp(_enabled: boolean): void {}
  /** @deprecated No-op for backward compatibility */
  setStackTrace(_enabled: boolean): void {}
  /** @deprecated No-op for backward compatibility */
  enableWithLevel(_scope: LogScope, _minLevel: LogLevel): void {}

  // ==================== Scene Registration ====================

  /**
   * Register the Babylon.js scene for scene graph capture
   */
  setScene(scene: Scene): void {
    this._scene = scene;
  }

  // ==================== Probe Recording ====================

  /**
   * Record a probe (silent - no console output unless streaming)
   * @param probeId Hierarchical probe ID (e.g., 'physics:body:create')
   * @param message Concise message string
   */
  probe(probeId: string, message: string): void {
    const entry: ProbeEntry = {
      probe: probeId,
      time: (performance.now() - this.startTime) / 1000,
      msg: message,
    };
    
    this.buffer.push(entry);
    
    // Stream to console if pattern matches
    if (this.streamPattern && matchGlob(this.streamPattern, probeId)) {
      console.log(`[${entry.time.toFixed(3)}s] ${probeId}: ${message}`);
    }
  }

  // ==================== Pull API ====================

  /**
   * Pull logs matching pattern, print to console, return entries
   * @param pattern Glob pattern (e.g., 'physics:*', '*:error', '*')
   * @param options Pull options
   */
  pull(pattern: string = '*', options: PullOptions = {}): ProbeEntry[] {
    let matches = this.buffer.filter(e => matchGlob(pattern, e.probe));
    
    // Apply last N limit
    if (options.last && matches.length > options.last) {
      matches = matches.slice(-options.last);
    }
    
    // Print to console
    if (matches.length === 0) {
      console.log(`[logger] No entries matching '${pattern}'`);
    } else {
      console.group(`[logger] ${matches.length} entries matching '${pattern}'`);
      for (const entry of matches) {
        console.log(`+${entry.time.toFixed(3)}s ${entry.probe}: ${entry.msg}`);
      }
      console.groupEnd();
    }
    
    // Clear if requested
    if (options.clear) {
      this.buffer = this.buffer.filter(e => !matchGlob(pattern, e.probe));
    }
    
    return matches;
  }

  // ==================== Clear API ====================

  /**
   * Clear buffer, optionally only matching pattern
   * @param pattern Optional glob pattern to clear
   */
  clear(pattern?: string): void {
    if (!pattern || pattern === '*') {
      const count = this.buffer.length;
      this.buffer = [];
      console.log(`[logger] Cleared ${count} entries`);
    } else {
      const before = this.buffer.length;
      this.buffer = this.buffer.filter(e => !matchGlob(pattern, e.probe));
      const removed = before - this.buffer.length;
      console.log(`[logger] Cleared ${removed} entries matching '${pattern}'`);
    }
  }

  // ==================== Stream API ====================

  /**
   * Enable real-time console output for matching probes
   * @param pattern Glob pattern to stream
   */
  stream(pattern: string): void {
    this.streamPattern = pattern;
    console.log(`[logger] Streaming probes matching '${pattern}'`);
  }

  /**
   * Disable real-time streaming
   */
  stopStream(): void {
    if (this.streamPattern) {
      console.log(`[logger] Stopped streaming '${this.streamPattern}'`);
      this.streamPattern = null;
    }
  }

  // ==================== Scene Graph API ====================

  /**
   * Get scene graph as compact TOON-like format
   * @param options Scene capture options
   */
  scene(options: SceneOptions = {}): SceneNode[] | SceneNode | null {
    if (!this._scene) {
      console.warn('[logger] No scene registered. Call logger.setScene(scene) first.');
      return null;
    }

    const include = options.include || [];
    const depth = options.depth ?? (options.around ? 1 : Infinity);

    // If around is specified, find the node and return its neighborhood
    if (options.around) {
      const targetNode = this.findNode(options.around);
      if (!targetNode) {
        console.warn(`[logger] Node '${options.around}' not found`);
        return null;
      }
      return this.captureNeighborhood(targetNode, depth, include);
    }

    // Full scene graph
    const roots = this._scene.rootNodes.filter(n => n.name !== '__root__');
    return roots.map(n => this.captureNode(n, depth, include));
  }

  private findNode(name: string): Node | null {
    if (!this._scene) return null;
    
    // Try exact match first
    let node = this._scene.getNodeByName(name);
    if (node) return node;
    
    // Try case-insensitive match
    for (const n of this._scene.getNodes()) {
      if (n.name.toLowerCase() === name.toLowerCase()) {
        return n;
      }
    }
    
    return null;
  }

  private captureNode(node: Node, depth: number, include: SceneAnnotation[]): SceneNode {
    const result: SceneNode = { name: node.name };
    
    // Add annotations
    for (const ann of include) {
      const value = this.getAnnotation(node, ann);
      if (value !== undefined) {
        result[ann] = value;
      }
    }
    
    // Recurse to children if depth allows
    if (depth > 0) {
      const children = node.getChildren();
      if (children.length > 0) {
        result.children = children.map(c => this.captureNode(c, depth - 1, include));
      }
    }
    
    return result;
  }

  private captureNeighborhood(node: Node, depth: number, include: SceneAnnotation[]): SceneNode {
    const result: SceneNode = { name: node.name };
    
    // Add annotations
    for (const ann of include) {
      const value = this.getAnnotation(node, ann);
      if (value !== undefined) {
        result[ann] = value;
      }
    }
    
    // Add parent chain (up to depth)
    if (depth > 0 && node.parent) {
      result._parent = this.captureNeighborhood(node.parent, depth - 1, include);
    }
    
    // Add children (down to depth)
    if (depth > 0) {
      const children = node.getChildren();
      if (children.length > 0) {
        result.children = children.map(c => this.captureNode(c, depth - 1, include));
      }
    }
    
    return result;
  }

  private getAnnotation(node: Node, annotation: SceneAnnotation): any {
    switch (annotation) {
      case 'position':
        if (node instanceof TransformNode) {
          const p = node.position;
          return { x: +p.x.toFixed(2), y: +p.y.toFixed(2), z: +p.z.toFixed(2) };
        }
        return undefined;
        
      case 'rotation':
        if (node instanceof TransformNode) {
          const r = node.rotationQuaternion;
          if (r) {
            return { x: +r.x.toFixed(3), y: +r.y.toFixed(3), z: +r.z.toFixed(3), w: +r.w.toFixed(3) };
          }
          const e = node.rotation;
          return { x: +e.x.toFixed(3), y: +e.y.toFixed(3), z: +e.z.toFixed(3) };
        }
        return undefined;
        
      case 'scaling':
        if (node instanceof TransformNode) {
          const s = node.scaling;
          return { x: +s.x.toFixed(2), y: +s.y.toFixed(2), z: +s.z.toFixed(2) };
        }
        return undefined;
        
      case 'visibility':
        if (node instanceof AbstractMesh) {
          return node.isVisible;
        }
        return undefined;
        
      case 'physics':
        if (node instanceof AbstractMesh && node.physicsBody) {
          // Basic physics info
          return { hasBody: true };
        }
        return undefined;
        
      case 'state':
        // Check for state machine state on node metadata
        const metadata = (node as any).metadata;
        if (metadata?.state) {
          return metadata.state;
        }
        return undefined;
        
      case 'metadata':
        const meta = (node as any).metadata;
        if (meta) {
          // Return shallow copy of metadata
          return { ...meta };
        }
        return undefined;
        
      default:
        return undefined;
    }
  }

  // ==================== Buffer Info ====================

  /**
   * Get buffer statistics
   */
  stats(): { count: number; probes: string[] } {
    const probes = [...new Set(this.buffer.map(e => e.probe))];
    return {
      count: this.buffer.length,
      probes: probes.sort(),
    };
  }
}

// ==================== Singleton Export ====================

export const logger = new Logger();

// ==================== Backward Compatibility Exports ====================
// These types and exports are DEPRECATED but kept for existing code.

// Deprecated: EventBus stub for old console-panel imports
class EventBusStub {
  on(_event: string, _handler: Function): void {}
  off(_event: string, _handler: Function): void {}
  emit(_event: string, _data?: any): void {}
}
export const loggerEventBus = new EventBusStub();

// Expose to window for Playwright access
if (typeof window !== 'undefined') {
  (window as any).logger = logger;
}
